import { getCartErrorMessage } from '@theme/cart-feedback';

class SBundleForm extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('change', (event) => {
      if (event.target instanceof HTMLSelectElement && event.target.matches('[data-bundle-variant]')) {
        this.#updateImage(event.target);
        this.#updatePrices();
      }
    }, opts);

    this.addEventListener('submit', (event) => {
      if (!window.Shopify?.actions?.updateCart) return;
      event.preventDefault();
      if (this.getAttribute('aria-busy') === 'true') return;
      this.#submit(event.target, event.submitter);
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #format(cents) {
    try {
      return new Intl.NumberFormat(this.dataset.locale || undefined, {
        style: 'currency',
        currency: this.dataset.currency || 'USD',
      }).format(cents / 100);
    } catch {
      return (cents / 100).toFixed(2);
    }
  }

  #priceOf(control) {
    if (control instanceof HTMLSelectElement) {
      return Number(control.selectedOptions[0]?.dataset.price || 0);
    }
    return Number(control.dataset.price || 0);
  }

  #updateImage(select) {
    const option = select.selectedOptions[0];
    const item = select.closest('[data-bundle-item]');
    const image = item?.querySelector('[data-bundle-image]') || item?.querySelector('a img');
    if (!option?.dataset.image || !(image instanceof HTMLImageElement)) return;

    // Drop the server srcset first, otherwise the browser keeps showing the original photo.
    const sizes = image.getAttribute('sizes');
    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    image.src = option.dataset.image;
    if (option.dataset.srcset) {
      image.sizes = sizes || '(min-width: 640px) 30vw, 90vw';
      image.srcset = option.dataset.srcset;
    }
    if (option.dataset.alt) image.alt = option.dataset.alt;
  }

  #updatePrices() {
    const keep = (100 - Number(this.dataset.discountPercent || 0)) / 100;
    let total = 0;
    let totalAfter = 0;

    for (const item of this.querySelectorAll('[data-bundle-item]')) {
      const control = item.querySelector('[data-bundle-variant]');
      if (!control) continue;
      const price = this.#priceOf(control);
      const after = item.hasAttribute('data-discounted') ? Math.floor(price * keep) : price;
      total += price;
      totalAfter += after;

      const compare = item.querySelector('[data-bundle-compare]');
      if (compare) compare.textContent = this.#format(price);
      const priceEl = item.querySelector('[data-bundle-price]');
      if (priceEl) priceEl.textContent = this.#format(after);
    }

    const totalEl = this.querySelector('[data-bundle-total]');
    if (totalEl) totalEl.textContent = this.#format(total);
    const afterEl = this.querySelector('[data-bundle-total-after]');
    if (afterEl) afterEl.textContent = this.#format(totalAfter);
  }

  async #submit(form, submitter) {
    const lines = [...form.querySelectorAll('[data-bundle-variant]')]
      .map((control) => String(control.value || '').trim())
      .filter(Boolean)
      .map((merchandiseId) => ({ merchandiseId, quantity: 1 }));
    if (!lines.length) return;

    this.setAttribute('aria-busy', 'true');
    submitter?.setAttribute('aria-disabled', 'true');
    this.#showError('');

    try {
      const result = await window.Shopify.actions.updateCart(
        { lines },
        { event: { context: 'product', detail: { source: 's-bundle-form' } } }
      );
      const message = result?.userErrors?.[0]?.message || '';
      if (message) {
        this.#showError(message);
      } else {
        if (this.closest('[data-reload-on-add]')) {
          // On the cart page, reload so the new lines and the bundle discount show in the cart itself.
          window.location.reload();
        } else {
          await window.Shopify.actions.openCart?.();
        }
      }
    } catch (error) {
      this.#showError(getCartErrorMessage(error) || this.dataset.errorMessage || '');
    } finally {
      this.removeAttribute('aria-busy');
      submitter?.removeAttribute('aria-disabled');
    }
  }

  #showError(message) {
    const error = this.querySelector('[data-bundle-error]');
    if (!error) return;
    error.textContent = message || '';
    error.hidden = !message;
  }
}

if (!customElements.get('s-bundle-form')) {
  customElements.define('s-bundle-form', SBundleForm);
}
